import React, { useContext } from "react";
import {
    Playlist,
    PlaylistItem,
    PlaylistProgress,
    PlaylistStatus,
} from "../../../../type";
import {
    QueryObserverLoadingResult,
    QueryObserverSuccessResult,
    useQuery,
    UseQueryResult,
} from "@tanstack/react-query";
import { LegacyLoader } from "../../../../../../../components/Loader/Loader";
import queryKeys from "../../../../api/queryKeys";
import { sessionStore } from "../../../../../../../contexts/SessionContext";
import usePlaylistAPIClient from "../../../../api/usePlaylistAPIClient";
import SimpleBar from "simplebar-react";
import PlaylistCard from "../PlaylistCard/PlaylistCard";
import DuplicateButton from "../DuplicateButton/DuplicateButton";
import ContainerBlock from "../../../../../../../design-system-components/ContainerBlock/ContainerBlock";
import ArchiveButton from "../ArchiveButton/ArchiveButton";

import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { authorizedItemType, filterWithStatus } from "../../../utils";
import useOnlineStatus from "../../../../../../../hooks/useOnlineStatus";

import "./PlaylistTabItem.scss";
import {
    Button,
    ButtonProps,
} from "@evidenceb/athena-common/design-system/Button";
import EmptyContainer from "../../../../../../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { defaultImages } from "../../../../../../../utils/defaultImages";

interface PlaylistItemContentProps
    extends React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    > {
    emptyContainerMain: {
        defaultText: string;
        button: ButtonProps;
        img?: string;
    };
    onPlaylistClick: (id: string) => void;
    playlistInPreview?: Playlist<PlaylistItem>;
    status: PlaylistStatus;
    onPlaylistInPreviewBlur: () => void;
}

const PlaylistItemContent = ({
    emptyContainerMain,
    status,
    onPlaylistClick,
    onPlaylistInPreviewBlur,
    playlistInPreview,
}: PlaylistItemContentProps) => {
    const {
        session: { userId },
    } = useContext(sessionStore);
    const isOnline = useOnlineStatus();
    const { isTabletOrSmaller } = useBreakpoints();
    const { getPlaylistProgress, getPlaylists } = usePlaylistAPIClient();

    const playlistsQuery = useQuery({
        queryKey: queryKeys.getPlaylists({ author_id: userId }),
        queryFn: getPlaylists,
        staleTime: Infinity,
    });
    let progressQuery: UseQueryResult<PlaylistProgress[]> | undefined =
        useQuery({
            queryKey: queryKeys.getPlaylistProgress({ teacher_id: userId }),
            queryFn: getPlaylistProgress,
            staleTime: 2 * 60 * 1000,
        });

    const tutosFlag = useFeatureFlag("tutosInPlaylist");
    const workshopsFlag = useFeatureFlag("workshopsInPlaylist");

    if (playlistsQuery.isError || playlistsQuery.isLoading)
        return <LegacyLoader />;
    if (progressQuery.isError) progressQuery = undefined;

    const playlists = playlistsQuery.data
        .filter(filterWithStatus(status))
        .filter(authorizedItemType(tutosFlag, workshopsFlag))
        .map(addProgress(progressQuery));

    return (
        <>
            {playlistsQuery.isLoading ? (
                <LegacyLoader />
            ) : (
                <div className="playlist-item-content__main-container">
                    {(!playlists || playlists.length === 0) && (
                        <EmptyContainer
                            img={emptyContainerMain.img}
                            defaultImg={
                                status === PlaylistStatus.DRAFT
                                    ? defaultImages.teacherResourcesPlaylistsDraft
                                    : status === PlaylistStatus.SENT
                                    ? defaultImages.teacherResourcesPlaylistsSent
                                    : status === PlaylistStatus.ARCHIVED
                                    ? defaultImages.teacherResourcesPlaylistsArchived
                                    : undefined
                            }
                        >
                            <p>{emptyContainerMain.defaultText}</p>
                            <Button {...emptyContainerMain.button} />
                        </EmptyContainer>
                    )}
                    {playlists && playlists.length > 0 && (
                        <ContainerBlock className="playlist-item-content__main-container-block">
                            {!isTabletOrSmaller && isOnline && (
                                <div className="playlist-item-content__main-container-action-tray">
                                    <DuplicateButton
                                        playlistInPreview={playlistInPreview}
                                        nbPlaylists={
                                            playlistsQuery.data?.length
                                        }
                                    />
                                    {status != PlaylistStatus.ARCHIVED && (
                                        <ArchiveButton
                                            playlistId={
                                                playlistInPreview?.learning_set_id
                                            }
                                            onArchive={onPlaylistInPreviewBlur}
                                        />
                                    )}
                                </div>
                            )}
                            <div className="playlist-item-content__playlist-container">
                                <SimpleBar
                                    style={{
                                        height: "100%",
                                    }}
                                >
                                    <ol className="playlist-item-content__list">
                                        {playlists.map((playlist) => {
                                            return (
                                                <li
                                                    key={
                                                        playlist.learning_set_id
                                                    }
                                                >
                                                    <PlaylistCard
                                                        playlist={playlist}
                                                        onPlaylistPress={() =>
                                                            onPlaylistClick(
                                                                playlist.learning_set_id
                                                            )
                                                        }
                                                        status={status}
                                                        idle={
                                                            playlistInPreview &&
                                                            playlistInPreview?.learning_set_id !==
                                                                playlist.learning_set_id
                                                        }
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </SimpleBar>
                            </div>
                        </ContainerBlock>
                    )}
                </div>
            )}
        </>
    );
};

export default PlaylistItemContent;

const addProgress =
    (
        playlistsProgress?:
            | QueryObserverSuccessResult<PlaylistProgress[] | undefined>
            | QueryObserverLoadingResult<PlaylistProgress[]>
    ) =>
    (playlist: Playlist<PlaylistItem>) => {
        const progress = playlistsProgress?.data?.find(
            (progress) => progress.id === playlist.learning_set_id
        );
        return { ...playlist, progress };
    };
